// frontend/src/pages/StudentTopicContent.js — FULL REPLACEMENT (Phase B)
// Orchestrator: MOBILE now uses the new Perplexity-style feed + article
// reader (StudentTopicFeed → StudentArticleReader). DESKTOP keeps the
// existing side-panel experience (StudentTopicContentDesktop) unchanged.
// Data fetching for desktop is unchanged except API_BASE now comes from
// services/api.js (removes the http:// IP fallback / mixed-content risk).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './studenttopiccontent.css';
import StudentTopicContentDesktop from './StudentTopicContentDesktop';
import StudentTopicFeed from './StudentTopicFeed';
import { API_BASE } from '../services/api.js';

const DESKTOP_BREAKPOINT = 1024;

const StudentTopicContent = ({ topicId, topicName, language, onBack }) => {
    const [isDesktop, setIsDesktop] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : false
    );

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ── Desktop data + handlers (unchanged behavior) ──
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [expandedContent, setExpandedContent] = useState(null);
    const [contentLoading, setContentLoading] = useState(false);
    const [fullscreenMedia, setFullscreenMedia] = useState(null);
    const cardRefs = useRef([]);

    const loadFlattened = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/content-page/topic/${topicId}/flattened?language=${language}`);
            if (!res.ok) throw new Error('Failed to load content');
            const data = await res.json();
            setCards(data.filter((card) => card.card_type !== 'block'));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [topicId, language]);

    useEffect(() => {
        if (isDesktop) loadFlattened();
    }, [isDesktop, loadFlattened]);

    const fetchContentForIndex = async (index) => {
        const card = cards[index];
        setContentLoading(true);
        setExpandedContent(null);
        try {
            const res = await fetch(`${API_BASE}/content-page/card/${card.card_type}/${card.card_id}?language=${language}`);
            if (!res.ok) throw new Error('Failed to load content');
            const data = await res.json();
            setExpandedContent(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setContentLoading(false);
        }
    };

    const toggleCard = async (index) => {
        if (expandedIndex === index) {
            setExpandedIndex(null);
            setExpandedContent(null);
            return;
        }
        setExpandedIndex(index);
        await fetchContentForIndex(index);
    };

    const goToIndex = async (index) => {
        if (index < 0 || index >= cards.length) return;
        setExpandedIndex(index);
        await fetchContentForIndex(index);
    };

    const openFullscreen = (media) => setFullscreenMedia(media);
    const closeFullscreen = () => setFullscreenMedia(null);

    // ── Mobile: new feed experience ──
    if (!isDesktop) {
        return (
            <StudentTopicFeed
                topicId={topicId}
                topicName={topicName}
                language={language}
                onBack={onBack}
            />
        );
    }

    // ── Desktop: existing experience ──
    return (
        <StudentTopicContentDesktop
            topicId={topicId}
            language={language}
            topicName={topicName}
            onBack={onBack}
            error={error}
            loading={loading}
            cards={cards}
            expandedIndex={expandedIndex}
            expandedContent={expandedContent}
            contentLoading={contentLoading}
            toggleCard={toggleCard}
            goToIndex={goToIndex}
            cardRefs={cardRefs}
            fullscreenMedia={fullscreenMedia}
            openFullscreen={openFullscreen}
            closeFullscreen={closeFullscreen}
        />
    );
};

export default StudentTopicContent;